import React, { useCallback, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { Input } from "@/src/components/ui/input";
import { Slider } from "@/src/components/ui/slider";

import { formatClock, formatRecordedDuration, parseClock } from "./liveFormat";

import type { JSX, KeyboardEvent, ReactNode } from "react";

import type { LiveReplayRange as ReplayRange } from "./live.types";

interface LiveReplayRangeProps {
  /** Recorded duration in seconds: the slider maximum. */
  totalSeconds: number;
  value: ReplayRange;
  disabled?: boolean;
  onChange: (range: ReplayRange) => void;
  children?: ReactNode;
}

interface ClockFieldProps {
  id: string;
  label: string;
  seconds: number;
  disabled: boolean;
  onCommit: (seconds: number) => void;
}

function ClockField({
  id,
  label,
  seconds,
  disabled,
  onCommit,
}: ClockFieldProps): JSX.Element {
  const [text, setText] = useState(formatClock(seconds));

  useEffect(() => {
    setText(formatClock(seconds));
  }, [seconds]);

  const commit = useCallback((): void => {
    const parsed = parseClock(text);
    if (parsed === null) setText(formatClock(seconds));
    else onCommit(parsed);
  }, [text, seconds, onCommit]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>): void => {
      if (event.key === "Enter") commit();
    },
    [commit],
  );

  return (
    <div className="flex flex-col gap-1">
      <label className="text-muted-foreground text-xs" htmlFor={id}>
        {label}
      </label>
      <Input
        className="w-28 font-mono"
        disabled={disabled}
        id={id}
        onBlur={commit}
        onChange={(event) => setText(event.target.value)}
        onKeyDown={handleKeyDown}
        value={text}
      />
    </div>
  );
}

function LiveReplayRange({
  totalSeconds,
  value,
  disabled = false,
  onChange,
  children,
}: LiveReplayRangeProps): JSX.Element {
  const { t } = useTranslation("editor-content");

  const setStart = useCallback(
    (start: number): void => {
      onChange({
        start: Math.min(Math.max(0, start), value.end - 1),
        end: value.end,
      });
    },
    [onChange, value.end],
  );

  const setEnd = useCallback(
    (end: number): void => {
      onChange({
        start: value.start,
        end: Math.max(Math.min(totalSeconds, end), value.start + 1),
      });
    },
    [onChange, totalSeconds, value.start],
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-medium">
          {t("liveRegie_replayRangeTitle")}
        </span>
        <span className="text-muted-foreground text-sm">
          {t("liveRegie_replayDuration", {
            duration: formatRecordedDuration(value.end - value.start),
          })}
        </span>
      </div>
      <Slider
        aria-label={t("liveRegie_replayRangeTitle")}
        disabled={disabled}
        max={Math.max(1, totalSeconds)}
        min={0}
        minStepsBetweenThumbs={1}
        step={1}
        value={[value.start, value.end]}
        onValueChange={([start = value.start, end = value.end]) =>
          onChange({ start, end })
        }
      />
      <div className="flex flex-wrap items-end gap-4">
        <ClockField
          disabled={disabled}
          id="live-replay-start"
          label={t("liveRegie_replayStart")}
          onCommit={setStart}
          seconds={value.start}
        />
        <ClockField
          disabled={disabled}
          id="live-replay-end"
          label={t("liveRegie_replayEnd")}
          onCommit={setEnd}
          seconds={value.end}
        />
        {children ? <div className="ml-auto">{children}</div> : null}
      </div>
    </div>
  );
}

export default LiveReplayRange;
